import { clearAuthSession, syncAuthSessionFromPayload } from './authSessionStore';

const EVENT_KEY = 'auth:change';
const LOGOUT_KEY = 'auth:logout-pending';
let epoch = 0;
let controller = new AbortController();
let channel;
let refreshPromise;
let syncPromise;
let sessionLoader;
let loggingOut = false;
const listeners = new Set();

function storage(action) {
  try { return typeof window !== 'undefined' ? action(window.localStorage) : undefined; } catch { return undefined; }
}

export const getAuthEpoch = () => epoch;
export const getAuthSignal = () => controller.signal;
export const isLogoutPending = () => loggingOut || storage((s) => s.getItem(LOGOUT_KEY)) === '1';
export const setSessionLoader = (loader) => { sessionLoader = loader; };
export const subscribeAuthChanges = (listener) => { listeners.add(listener); return () => listeners.delete(listener); };

export function invalidateAuthRequests() {
  epoch += 1;
  controller.abort();
  controller = new AbortController();
}

export function clearLegacyTokens() {
  if (typeof window === 'undefined') return;
  for (const name of ['localStorage', 'sessionStorage']) {
    try { ['token', 'refreshToken'].forEach((key) => window[name].removeItem(key)); } catch { /* storage can be unavailable */ }
  }
}

export function publishAuthChange(type) {
  const event = { type, id: `${Date.now()}-${Math.random()}` };
  if (channel) channel.postMessage(event);
  else storage((s) => s.setItem(EVENT_KEY, JSON.stringify(event)));
}

export function startAuthEvents() {
  const receive = (event) => {
    if (!['login', 'logout', 'restaurant'].includes(event?.type)) return;
    invalidateAuthRequests();
    clearAuthSession({ reset: event.type !== 'logout' });
    listeners.forEach((listener) => listener(event.type));
  };
  const onStorage = (event) => {
    if (event.key !== EVENT_KEY || !event.newValue) return;
    try { receive(JSON.parse(event.newValue)); } catch { /* malformed notification */ }
  };
  if (typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel('gap-auth');
    channel.onmessage = (event) => receive(event.data);
  }
  window.addEventListener('storage', onStorage);
  return () => { channel?.close(); channel = null; window.removeEventListener('storage', onStorage); };
}

export function beginLogout() {
  loggingOut = true;
  storage((s) => s.setItem(LOGOUT_KEY, '1'));
  invalidateAuthRequests();
  clearAuthSession();
  publishAuthChange('logout');
}

export function finishLogout(success) {
  loggingOut = false;
  if (success) storage((s) => s.removeItem(LOGOUT_KEY));
}

export function acceptLogin(payload) {
  invalidateAuthRequests();
  clearLegacyTokens();
  finishLogout(true);
  const session = syncAuthSessionFromPayload(payload);
  publishAuthChange('login');
  return session;
}

export function acceptRestaurant(payload) {
  invalidateAuthRequests();
  clearAuthSession({ reset: true });
  const session = syncAuthSessionFromPayload(payload);
  publishAuthChange('restaurant');
  return session;
}

export async function syncChangedSession() {
  if (!syncPromise) {
    invalidateAuthRequests();
    clearAuthSession({ reset: true });
    syncPromise = Promise.resolve().then(() => sessionLoader?.()).finally(() => { syncPromise = null; });
  }
  return syncPromise;
}

export function refreshOnce(run) {
  if (!refreshPromise) {
    const task = async () => {
      if (isLogoutPending()) throw new Error('Logout pendente');
      return run();
    };
    const locks = typeof navigator !== 'undefined' ? navigator.locks : null;
    refreshPromise = Promise.resolve(locks ? locks.request('gap-auth-refresh', task) : task())
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}
